import React, { useState, useRef, useEffect } from "react";
import { Sort2Icon } from "~/assets/icons";
import jobApi, { ITradeType } from "~/apis/job";
import userManagementApi, { IEmployee } from "~/apis/userManagement";
import styles from "./JobSortFilter.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

// Search Icon Component
const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M18.4608 20.0177L11.4546 13.0115C10.8985 13.4564 10.2591 13.8085 9.53623 14.068C8.81337 14.3275 8.04417 14.4573 7.22863 14.4573C5.20832 14.4573 3.49866 13.7574 2.09964 12.3576C0.700621 10.9579 0.000741986 9.2482 5.88411e-07 7.22863C-0.000740809 5.20906 0.699139 3.4994 2.09964 2.09964C3.50014 0.69988 5.2098 0 7.22863 0C9.24745 0 10.9575 0.69988 12.3587 2.09964C13.76 3.4994 14.4595 5.20906 14.4573 7.22863C14.4573 8.04417 14.3275 8.81337 14.068 9.53623C13.8085 10.2591 13.4564 10.8985 13.0115 11.4546L20.0177 18.4608L18.4608 20.0177ZM7.22863 12.2331C8.61875 12.2331 9.80054 11.7467 10.774 10.774C11.7474 9.80128 12.2338 8.61949 12.2331 7.22863C12.2323 5.83777 11.746 4.65635 10.774 3.68438C9.80202 2.7124 8.62023 2.22568 7.22863 2.22419C5.83703 2.22271 4.65561 2.70944 3.68438 3.68438C2.71315 4.65931 2.22642 5.84073 2.22419 7.22863C2.22197 8.61652 2.7087 9.79831 3.68438 10.774C4.66006 11.7497 5.84147 12.236 7.22863 12.2331Z"
      fill="white"
    />
  </svg>
);

// Chevron Down Icon Component
const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="9"
    height="6"
    viewBox="0 0 9 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z"
      fill="white"
    />
  </svg>
);

// Close Icon Component
const CloseIcon: React.FC<{ className?: string; onClick?: (e: React.MouseEvent) => void }> = ({ className, onClick }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    onClick={onClick}
  >
    <path
      d="M8.75 0.75L0.75 8.75M0.75 0.75L8.75 8.75"
      stroke="#FF2D55"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Check Icon Component - Custom checkbox checked state
const CheckIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="5" fill="#2D80FC"/>
    <path d="M18 7.5L9.75 16.5L6 12.4091" stroke="#F9FBFE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export interface JobSortFilterValue {
  type: 'all' | 'jobType' | 'tradeType' | 'salesRep' | 'assignee' | 'subcontractor';
  value?: string | string[]; // Single value or array of values for multiple selections
}

interface JobSortFilterProps {
  value: JobSortFilterValue;
  onChange: (value: JobSortFilterValue) => void;
  placeholder?: string;
  className?: string;
}

const JobSortFilter: React.FC<JobSortFilterProps> = ({
  value,
  onChange,
  placeholder = "Sort By",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchType, setActiveSearchType] = useState<'salesRep' | 'assignee' | null>(null);
  const [showTradeTypeSubmenu, setShowTradeTypeSubmenu] = useState(false);
  const [tradeTypes, setTradeTypes] = useState<ITradeType[]>([]);
  const [isLoadingTradeTypes, setIsLoadingTradeTypes] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [activeFilterCategory, setActiveFilterCategory] = useState<'type' | 'salesRep' | 'subcontractor' | 'assignee' | null>('type');
  const [tempSelectedJobTypes, setTempSelectedJobTypes] = useState<string[]>([]);
  const [tempSelectedSalesReps, setTempSelectedSalesReps] = useState<string[]>([]);
  const [tempSelectedSubcontractors, setTempSelectedSubcontractors] = useState<string[]>([]);
  const [tempSelectedAssignees, setTempSelectedAssignees] = useState<string[]>([]);
  const [filterSearchText, setFilterSearchText] = useState("");
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize temp selections when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Load selections from value prop (support both string and array)
      if (value.type === 'jobType' && value.value) {
        const jobTypes = Array.isArray(value.value) ? value.value : [value.value];
        setTempSelectedJobTypes(jobTypes);
        setActiveFilterCategory('type');
      } else if (value.type === 'salesRep' && value.value) {
        const salesReps = Array.isArray(value.value) ? value.value : [value.value];
        setTempSelectedSalesReps(salesReps);
        setActiveFilterCategory('salesRep');
      } else if (value.type === 'assignee' && value.value) {
        const assignees = Array.isArray(value.value) ? value.value : [value.value];
        setTempSelectedAssignees(assignees);
        setActiveFilterCategory('assignee');
      } else if (value.type === 'subcontractor' && value.value) {
        const subcontractors = Array.isArray(value.value) ? value.value : [value.value];
        setTempSelectedSubcontractors(subcontractors);
        setActiveFilterCategory('subcontractor');
      } else {
        // Keep existing selections, just reset active category
        setActiveFilterCategory('type');
      }
      setFilterSearchText("");
    }
  }, [isOpen, value]);

  // Fetch employees when filter category changes
  useEffect(() => {
    const fetchEmployees = async () => {
      if (activeFilterCategory === 'salesRep' || activeFilterCategory === 'assignee' || activeFilterCategory === 'subcontractor') {
        setIsLoadingEmployees(true);
        try {
          const params: {
            page: number;
            limit: number;
            status: 'ACTIVE';
            role?: string;
          } = {
            page: 1,
            limit: 50,
            status: 'ACTIVE',
            ...(activeFilterCategory === 'subcontractor' && { role: '00004' }),
          };

          const response = await userManagementApi.getEmployees(params);
          if (response.data?.data?.responses) {
            setEmployees(response.data.data.responses);
          }
        } catch (error) {
          console.error("Error fetching employees:", error);
        } finally {
          setIsLoadingEmployees(false);
        }
      } else {
        setEmployees([]);
      }
    };

    if (isOpen) {
      fetchEmployees();
    }
  }, [activeFilterCategory, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveSearchType(null);
        setSearchInput("");
        setShowTradeTypeSubmenu(false);
        setActiveFilterCategory('type');
        setFilterSearchText("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Fetch trade types when component mounts
  useEffect(() => {
    const fetchTradeTypes = async () => {
      setIsLoadingTradeTypes(true);
      try {
        const response = await jobApi.getTradeTypes();
        if (response.data?.data?.responses) {
          setTradeTypes(response.data.data.responses);
        }
      } catch (error) {
        console.error("Error fetching trade types:", error);
      } finally {
        setIsLoadingTradeTypes(false);
      }
    };
    fetchTradeTypes();
  }, []);

  // Focus input when search type is activated
  useEffect(() => {
    if (activeSearchType && inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeSearchType]);

  // Calculate dropdown position to prevent overflow
  useEffect(() => {
    if (isOpen && dropdownRef.current && dropdownMenuRef.current) {
      const containerRect = dropdownRef.current.getBoundingClientRect();
      const dropdownWidth = 600; // width của dropdown
      const viewportWidth = window.innerWidth;
      const spaceOnRight = viewportWidth - containerRect.right;
      const spaceOnLeft = containerRect.left;

      // Nếu không đủ chỗ bên phải, align sang phải của container
      if (spaceOnRight < dropdownWidth && spaceOnLeft > spaceOnRight) {
        setAlignRight(true);
      } else {
        setAlignRight(false);
      }
    }
  }, [isOpen]);

  const getDisplayText = () => {
    if (value.type === 'all') return 'All';
    if (value.type === 'jobType') {
      if (!value.value) return 'Job Type';
      if (Array.isArray(value.value)) {
        return value.value.length > 1 ? `${value.value.length} types` : value.value[0];
      }
      return value.value;
    }
    if (value.type === 'tradeType') {
      const tradeTypeValue = Array.isArray(value.value) ? value.value[0] : value.value;
      const selectedTradeType = tradeTypes.find(t => t.id === tradeTypeValue);
      return selectedTradeType ? selectedTradeType.name : 'Trade Type';
    }
    if (value.type === 'salesRep') {
      if (!value.value) return 'Sales Rep';
      if (Array.isArray(value.value)) {
        return value.value.length > 1 ? `${value.value.length} sales reps` : `Sales Rep: ${value.value[0]}`;
      }
      return `Sales Rep: ${value.value}`;
    }
    if (value.type === 'assignee') {
      if (!value.value) return 'Assignee';
      if (Array.isArray(value.value)) {
        return value.value.length > 1 ? `${value.value.length} assignees` : `Assignee: ${value.value[0]}`;
      }
      return `Assignee: ${value.value}`;
    }
    if (value.type === 'subcontractor') {
      if (!value.value) return 'Subcontractor';
      if (Array.isArray(value.value)) {
        return value.value.length > 1 ? `${value.value.length} subcontractors` : `Subcontractor: ${value.value[0]}`;
      }
      return `Subcontractor: ${value.value}`;
    }
    return placeholder;
  };

  const handleSelect = (type: 'all' | 'jobType' | 'tradeType' | 'salesRep' | 'assignee', optionValue?: string) => {
    if (type === 'salesRep' || type === 'assignee') {
      setActiveSearchType(type);
      const searchValue = value.type === type && value.value 
        ? (Array.isArray(value.value) ? value.value[0] : value.value)
        : "";
      setSearchInput(searchValue);
    } else if (type === 'tradeType') {
      setShowTradeTypeSubmenu(true);
    } else {
      onChange({
        type,
        value: optionValue,
      });
      setIsOpen(false);
      setActiveSearchType(null);
      setSearchInput("");
      setShowTradeTypeSubmenu(false);
    }
  };

  const handleTradeTypeSelect = (tradeTypeId: string, tradeTypeName: string) => {
    onChange({
      type: 'tradeType',
      value: tradeTypeId,
    });
    setIsOpen(false);
    setShowTradeTypeSubmenu(false);
    setActiveSearchType(null);
    setSearchInput("");
  };

  const handleSearchSubmit = () => {
    if (activeSearchType) {
      if (searchInput.trim()) {
        onChange({
          type: activeSearchType,
          value: searchInput.trim(),
        });
      } else {
        // Clear filter - reset to "All"
        onChange({
          type: 'all',
        });
      }
      setIsOpen(false);
      setActiveSearchType(null);
      setSearchInput("");
      setShowTradeTypeSubmenu(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    } else if (e.key === 'Escape') {
      setActiveSearchType(null);
      setSearchInput("");
    }
  };

  const jobTypeOptions = ['Commercial', 'Insurance', 'Residential'];
  
  const filteredJobTypeOptions = filterSearchText
    ? jobTypeOptions.filter(option => 
        option.toLowerCase().includes(filterSearchText.toLowerCase())
      )
    : jobTypeOptions;

  const handleJobTypeToggle = (jobType: string) => {
    setTempSelectedJobTypes(prev => 
      prev.includes(jobType)
        ? prev.filter(t => t !== jobType)
        : [...prev, jobType]
    );
  };

  const handleSelectAll = () => {
    if (tempSelectedJobTypes.length === filteredJobTypeOptions.length) {
      setTempSelectedJobTypes([]);
    } else {
      setTempSelectedJobTypes([...filteredJobTypeOptions]);
    }
  };

  // Get current temp selections based on active category
  const getCurrentTempSelections = () => {
    switch (activeFilterCategory) {
      case 'salesRep':
        return tempSelectedSalesReps;
      case 'subcontractor':
        return tempSelectedSubcontractors;
      case 'assignee':
        return tempSelectedAssignees;
      default:
        return [];
    }
  };

  // Set current temp selections based on active category
  const setCurrentTempSelections = (selections: string[]) => {
    switch (activeFilterCategory) {
      case 'salesRep':
        setTempSelectedSalesReps(selections);
        break;
      case 'subcontractor':
        setTempSelectedSubcontractors(selections);
        break;
      case 'assignee':
        setTempSelectedAssignees(selections);
        break;
    }
  };

  const handleEmployeeToggle = (employeeId: string) => {
    const currentSelections = getCurrentTempSelections();
    const newSelections = currentSelections.includes(employeeId)
      ? currentSelections.filter(id => id !== employeeId)
      : [...currentSelections, employeeId];
    setCurrentTempSelections(newSelections);
  };

  const handleSelectAllEmployees = () => {
    const currentSelections = getCurrentTempSelections();
    if (currentSelections.length === filteredEmployees.length) {
      setCurrentTempSelections([]);
    } else {
      setCurrentTempSelections(filteredEmployees.map(emp => emp.id.toString()));
    }
  };

  const handleClear = () => {
    if (activeFilterCategory === 'type') {
      setTempSelectedJobTypes([]);
    } else {
      setCurrentTempSelections([]);
    }
    setFilterSearchText("");
  };

  const handleApply = () => {
    if (activeFilterCategory === 'type') {
      if (tempSelectedJobTypes.length === 0) {
        onChange({ type: 'all' });
      } else if (tempSelectedJobTypes.length === 1) {
        onChange({ type: 'jobType', value: tempSelectedJobTypes[0] });
      } else {
        // Multiple selections: send array
        onChange({ type: 'jobType', value: tempSelectedJobTypes });
      }
    } else if (activeFilterCategory === 'salesRep') {
      if (tempSelectedSalesReps.length === 0) {
        onChange({ type: 'all' });
      } else if (tempSelectedSalesReps.length === 1) {
        onChange({ type: 'salesRep', value: tempSelectedSalesReps[0] });
      } else {
        // Multiple selections: send array
        onChange({ type: 'salesRep', value: tempSelectedSalesReps });
      }
    } else if (activeFilterCategory === 'subcontractor') {
      if (tempSelectedSubcontractors.length === 0) {
        onChange({ type: 'all' });
      } else if (tempSelectedSubcontractors.length === 1) {
        onChange({ type: 'subcontractor', value: tempSelectedSubcontractors[0] });
      } else {
        // Multiple selections: send array
        onChange({ type: 'subcontractor', value: tempSelectedSubcontractors });
      }
    } else if (activeFilterCategory === 'assignee') {
      if (tempSelectedAssignees.length === 0) {
        onChange({ type: 'all' });
      } else if (tempSelectedAssignees.length === 1) {
        onChange({ type: 'assignee', value: tempSelectedAssignees[0] });
      } else {
        // Multiple selections: send array
        onChange({ type: 'assignee', value: tempSelectedAssignees });
      }
    }
    setIsOpen(false);
  };

  const handleFilterChipClick = (category: 'type' | 'salesRep' | 'subcontractor' | 'assignee') => {
    setActiveFilterCategory(category);
    setFilterSearchText("");
    // Keep existing selections when switching categories - each tab maintains its own state
  };

  const handleRemoveFilterChip = (category: 'type' | 'salesRep' | 'subcontractor' | 'assignee', e: React.MouseEvent) => {
    e.stopPropagation();
    if (category === 'type') {
      setTempSelectedJobTypes([]);
      onChange({ type: 'all' });
    } else if (category === 'salesRep') {
      setTempSelectedSalesReps([]);
      onChange({ type: 'all' });
    } else if (category === 'subcontractor') {
      setTempSelectedSubcontractors([]);
      onChange({ type: 'all' });
    } else if (category === 'assignee') {
      setTempSelectedAssignees([]);
      onChange({ type: 'all' });
    }
  };

  const isFilterChipActive = (category: 'type' | 'salesRep' | 'subcontractor' | 'assignee') => {
    if (category === 'type') {
      return value.type === 'jobType' && value.value !== undefined && 
        (Array.isArray(value.value) ? value.value.length > 0 : true);
    }
    if (category === 'salesRep') {
      return value.type === 'salesRep' && value.value !== undefined && 
        (Array.isArray(value.value) ? value.value.length > 0 : true);
    }
    if (category === 'assignee') {
      return value.type === 'assignee' && value.value !== undefined && 
        (Array.isArray(value.value) ? value.value.length > 0 : true);
    }
    if (category === 'subcontractor') {
      return value.type === 'subcontractor' && value.value !== undefined && 
        (Array.isArray(value.value) ? value.value.length > 0 : true);
    }
    return false;
  };

  // Check if a category has more than 1 selection (for showing X icon)
  const hasMultipleSelections = (category: 'type' | 'salesRep' | 'subcontractor' | 'assignee') => {
    if (category === 'type') {
      return tempSelectedJobTypes.length >= 1;
    }
    if (category === 'salesRep') {
      return tempSelectedSalesReps.length >= 1;
    }
    if (category === 'subcontractor') {
      return tempSelectedSubcontractors.length >=  1;
    }
    if (category === 'assignee') {
      return tempSelectedAssignees.length >= 1;
    }
    return false;
  };

  const filteredEmployees = filterSearchText
    ? employees.filter(emp => {
        const fullName = `${emp.profile?.firstName || ''} ${emp.profile?.lastName || ''}`.trim() || emp.username || '';
        const username = emp.username || '';
        const searchText = filterSearchText.toLowerCase();
        return (fullName && fullName.toLowerCase().includes(searchText)) ||
               (username && username.toLowerCase().includes(searchText));
      })
    : employees;

  const options = [
    { type: 'all' as const, label: 'All' },
    { type: 'jobType' as const, label: 'Commercial', value: 'Commercial' },
    { type: 'jobType' as const, label: 'Residential', value: 'Residential' },
    { type: 'jobType' as const, label: 'Insurance', value: 'Insurance' },
    { type: 'tradeType' as const, label: 'Trade Type', hasSubmenu: true },
    { type: 'salesRep' as const, label: 'Sales Rep', hasSearch: true },
    { type: 'assignee' as const, label: 'Assignee', hasSearch: true },
  ];

  return (
    <div ref={dropdownRef} className={cx("container", className)}>
      {/* Main Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cx("main-button", { active: isOpen })}
      >
        <span className="text-[#2D80FC] text-sm font-medium">
          Filter
        </span>
        <svg className={cx("icon")} width="20" height="18" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.75 0.75L0.75 0.75L8.75 10.21V16.75L12.75 18.75L12.75 10.21L20.75 0.75Z" stroke="#2D80FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>

      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className={cx("dropdown-backdrop")}
            onClick={() => {
              setIsOpen(false);
              setActiveSearchType(null);
              setSearchInput("");
              setShowTradeTypeSubmenu(false);
            }}
          />

          {/* Dropdown Content */}
          <div
            ref={dropdownMenuRef}
            className={cx("dropdown", { "align-right": alignRight })}
          >
            <div className={cx("filter-panel")}>
              {/* Title */}
              <div className={cx("filter-title")}>Filter</div>

              {/* Filter Category Chips */}
              <div className={cx("filter-chips")}>
                {(['type', 'salesRep', 'subcontractor', 'assignee'] as const).map((category) => {
                  const isActive = activeFilterCategory === category || isFilterChipActive(category);
                  const hasMultiple = hasMultipleSelections(category);
                  const label = category === 'type' ? 'Type' : 
                               category === 'salesRep' ? 'Sales rep' :
                               category === 'subcontractor' ? 'Subcontractor' : 'Assignee';
                  
                  return (
                    <button
                      key={category}
                      onClick={() => handleFilterChipClick(category)}
                      className={cx("filter-chip", { active: isActive, "has-remove": hasMultiple })}
                    >
                      <span>{label}</span>
                      {hasMultiple && (
                        <CloseIcon 
                          className={cx("chip-remove-icon")}
                          onClick={(e) => handleRemoveFilterChip(category, e)}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Search Bar */}
              <div className={cx("filter-search-wrapper")}>
                <SearchIcon className={cx("search-icon")} />
                <input
                  type="text"
                  value={filterSearchText}
                  onChange={(e) => setFilterSearchText(e.target.value)}
                  placeholder="Search"
                  className={cx("filter-search-input")}
                />
              </div>

              {/* Checkbox List */}
              {activeFilterCategory === 'type' && (
                <div className={cx("checkbox-list")}>
                  {/* Select All */}
                  <label className={cx("checkbox-item", "select-all-item")} onClick={handleSelectAll}>
                    <div className={cx("checkbox", { 
                      checked: tempSelectedJobTypes.length === filteredJobTypeOptions.length && filteredJobTypeOptions.length > 0 
                    })}>
                      {tempSelectedJobTypes.length === filteredJobTypeOptions.length && filteredJobTypeOptions.length > 0 && (
                        <CheckIcon />
                      )}
                    </div>
                    <span className={cx("checkbox-label")}>
                      Select all ({tempSelectedJobTypes.length})
                    </span>
                  </label>

                  {/* Individual Options */}
                  {filteredJobTypeOptions.map((option) => {
                    const isChecked = tempSelectedJobTypes.includes(option);
                    return (
                      <label 
                        key={option} 
                        className={cx("checkbox-item")}
                        onClick={() => handleJobTypeToggle(option)}
                      >
                        <div className={cx("checkbox", { checked: isChecked })}>
                          {isChecked && <CheckIcon />}
                        </div>
                        <span className={cx("checkbox-label")}>{option}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Employees List */}
              {(activeFilterCategory === 'salesRep' || activeFilterCategory === 'assignee' || activeFilterCategory === 'subcontractor') && (
                <div className={cx("checkbox-list")}>
                  {isLoadingEmployees ? (
                    <div className={cx("loading-state")}>Loading...</div>
                  ) : (
                    <>
                      {/* Select All */}
                      {filteredEmployees.length > 0 && (() => {
                        const currentSelections = getCurrentTempSelections();
                        return (
                          <label className={cx("checkbox-item", "select-all-item")} onClick={handleSelectAllEmployees}>
                            <div className={cx("checkbox", { 
                              checked: currentSelections.length === filteredEmployees.length && filteredEmployees.length > 0 
                            })}>
                              {currentSelections.length === filteredEmployees.length && filteredEmployees.length > 0 && (
                                <CheckIcon />
                              )}
                            </div>
                            <span className={cx("checkbox-label")}>
                              Select all ({currentSelections.length})
                            </span>
                          </label>
                        );
                      })()}

                      {/* Individual Employees */}
                      {filteredEmployees.length === 0 && !isLoadingEmployees ? (
                        <div className={cx("empty-state")}>No employees found</div>
                      ) : (
                        filteredEmployees.map((employee) => {
                          const employeeId = employee.id.toString();
                          const currentSelections = getCurrentTempSelections();
                          const isChecked = currentSelections.includes(employeeId);
                          const fullName = `${employee.profile?.firstName || ''} ${employee.profile?.lastName || ''}`.trim() || employee.username || '';
                          
                          return (
                            <label 
                              key={employee.id} 
                              className={cx("checkbox-item")}
                              onClick={() => handleEmployeeToggle(employeeId)}
                            >
                              <div className={cx("checkbox", { checked: isChecked })}>
                                {isChecked && <CheckIcon />}
                              </div>
                              <span className={cx("checkbox-label")}>{fullName}</span>
                            </label>
                          );
                        })
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className={cx("filter-actions")}>
                <button
                  onClick={handleClear}
                  className={cx("clear-button")}
                >
                  Clear
                </button>
                <button
                  onClick={handleApply}
                  className={cx("apply-button")}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default JobSortFilter;
